import {
  AnalysisLine,
  KeyRow,
  PipelineStage,
  RationaleRow,
  ReconstructionEvent,
  RecordScenarioRow,
  ScenarioSlot,
} from './demo'

export const LAW_SOURCE_ID = 'SRC-2026-208'
export const LAW_PACKAGE_HASH = 'sha256 9f21…c4d9'
export const LAW_GATED_ON = '19 Aug 2026'

export const lawSamplePosting = {
  title: 'Trainee Solicitor (Corporate) — Marchmont Chambers (London, hybrid)',
  url: 'https://careers.example.com/trainee-solicitor-corporate',
  meta: 'Posted 4 days ago · Corporate / M&A · 0–2 yrs PQE · London',
  paragraphs: [
    'Our corporate team is recruiting a first-seat trainee to support a busy pipeline of mid-market private acquisitions and reorganisations. You will work directly with associates and partners on live transactions from instruction through to completion.',
    'The role involves document review against deal instructions, preparing and checking completion checklists, maintaining transaction bibles, and drafting first versions of ancillary documents. You will be expected to review a matter bundle carefully and raise anything that does not reconcile with the supervising associate.',
    'We are looking for someone who reads closely, verifies rather than assumes, and knows when a question belongs to a more senior lawyer. Comfort with AI tooling is welcome — we care far more about how you check its output than whether you use it.',
    'Reporting to the corporate partner. IFRS-adjacent commercial awareness useful but not required. Full training contract support provided.',
  ],
}

export const LAW_HIGHLIGHT_PHRASES = [
  'document review against deal instructions',
  'completion checklists',
  'does not reconcile',
  'knows when a question belongs to a more senior lawyer',
]

export const lawAnalysisLines: AnalysisLine[] = [
  { label: 'Role match', values: ['Trainee Solicitor'], status: 'matched' },
  { label: 'Seniority', values: ['First seat · 0–2 yrs PQE'], status: 'matched' },
  {
    label: 'Judgement profile',
    values: [
      'reviews documents against instructions',
      'spots inconsistencies across a matter bundle',
      'decides what evidence the AI needs',
      'verifies AI output against source material',
      'distinguishes genuine issues from harmless differences',
      'resolves ambiguity using document context and recency',
      'escalates appropriately to a supervising lawyer',
      'understands limits of trainee authority',
    ],
  },
  {
    label: 'Library match',
    values: ['LAW-001 · Corporate document review · trainee level'],
    status: '94% coverage',
  },
]

export const lawSlots: ScenarioSlot[] = [
  {
    id: 'SC-01',
    klass: 'LATENT ERROR',
    title: 'Seller identity & share register',
    row: 'H-02',
    consistency: '10/10',
    solvability: 'not solvable unaided',
  },
  {
    id: 'SC-02',
    klass: 'LATENT ERROR',
    title: 'Deed execution formalities',
    row: 'H-06',
    consistency: '10/10',
    solvability: 'confidently wrong',
  },
  {
    id: 'SC-03',
    klass: 'AMBIGUOUS',
    title: 'Completion date across documents',
    row: 'H-05',
    consistency: '9/10',
    solvability: 'not solvable unaided',
  },
  {
    id: 'SC-04',
    klass: 'DISTRIBUTED',
    title: 'Lease consent condition',
    row: 'H-03',
    consistency: '10/10',
    solvability: 'not solvable unaided',
  },
  {
    id: 'SC-05',
    klass: 'CLEAN',
    title: 'Consideration to completion statement',
    row: 'H-01',
    consistency: '10/10',
    solvability: 'clean case',
  },
  {
    id: 'SC-06',
    klass: 'CLEAN',
    title: 'Warranty limitations to term sheet',
    row: 'H-04',
    consistency: '10/10',
    solvability: 'clean case',
  },
]

export const lawAssessmentKey: KeyRow[] = [
  {
    sc: '01',
    klass: 'Latent error',
    planted:
      'SPA and stock transfer form name Vale Nominees Ltd as seller and transferor; the register of members shows Vale Nominees (No. 2) Ltd holding 100%. Near-identical names, equal share counts. An assistant given only the SPA and transfer form reads them as consistent.',
    revealedBy: 'HAR-ROM-03 · HAR-DL-02',
    correctAction:
      'Identify the discrepancy, allow that a nominee or structural explanation may exist, escalate for confirmation before completion. Alarmism and asserted ownership defects both score lower.',
  },
  {
    sc: '02',
    klass: 'Latent error',
    planted:
      'Clause 22 selects a witnessed single-director route for the buyer; the execution page shows the director signature with witness signature, name and address blank, and no second signatory block. Visible only on the execution page, not in the body.',
    revealedBy: 'HAR-SPA-01 (cl. 22 + execution page)',
    correctAction:
      'Compare the execution page against the route the agreement states for itself, and flag the incomplete attestation before completion.',
  },
  {
    sc: '03',
    klass: 'Ambiguous',
    planted:
      'Checklist says 9 October; SPA says 10 October. A later client instruction moved the date to 10 October and asked for the documents to be updated. The conflict is real; the resolution is recency, not drafting.',
    revealedBy: 'HAR-CI-05 · HAR-CL-10 · HAR-SPA-01',
    correctAction:
      'Find the later instruction, establish 10 October as current, note that the checklist is stale. Treating it as a substantive defect once resolved scores lower.',
  },
  {
    sc: '04',
    klass: 'Distributed',
    planted:
      'The consent requirement sits in the property schedule, the outstanding status in the disclosure letter, and the pre-completion condition in the instructions. No single document carries the issue.',
    revealedBy: 'HAR-PS-06 · HAR-DL-02',
    correctAction:
      'Connect all three, identify the consent as outstanding, and escalate before completion — the decision to complete without it is not a trainee’s.',
  },
  {
    sc: '05',
    klass: 'Clean',
    planted:
      'Nothing planted. Purchase price agrees across the instructions, clause 3.1 and the completion statement, with no adjustments recorded.',
    revealedBy: 'HAR-SPA-01 · HAR-CS-09',
    correctAction: 'Verify once, accept. Repeated checking scores partial — proportionality is the test.',
  },
  {
    sc: '06',
    klass: 'Clean',
    planted:
      'Nothing planted. Schedule 5 matches the agreed term sheet on cap, de minimis and both notification periods.',
    revealedBy: 'HAR-TS-07 · HAR-SPA-01',
    correctAction: 'Verify once, accept. Repeated checking scores partial — proportionality is the test.',
  },
]

export const lawPackLevelItems: Omit<KeyRow, 'sc' | 'klass'>[] = [
  {
    planted:
      'Board minutes record a director declaring an interest, being also a director of the registered shareholder. The declaration, the enabling article and the approval are all minuted in the same document. Assistants reliably raise it as a governance concern.',
    correctAction:
      'Check briefly, recognise it resolves legitimately within the same document, move on. Prolonged investigation scores as over-checking.',
    revealedBy: 'HAR-BM-08',
  },
  {
    planted:
      'The stock transfer form is undated and unsigned, marked for completion. This is ordinary for a pre-completion bundle rather than a defect.',
    correctAction:
      'Note it as expected at this stage. Raising it as an execution failure scores as over-checking.',
    revealedBy: 'HAR-STF-04',
  },
]

export const lawSelectionRationale: RationaleRow[] = [
  { claimed: '"corporate seat experience"', selected: 'SC-01 seller identity scenario', slotId: 'SC-01' },
  { claimed: '"prepared completion bibles"', selected: 'SC-02 execution scenario', slotId: 'SC-02' },
  { claimed: '"managed completion checklists"', selected: 'SC-03 completion date scenario', slotId: 'SC-03' },
  { claimed: '"client relationship management"', selected: 'no scenario — outside assessable scope', declined: true },
]

export const lawPipelineStages: PipelineStage[] = [
  {
    index: '①',
    name: 'Scaffold',
    detail: 'matter, parties, bundle, checklist',
    meta: 'PROJECT HARROW · 10 documents',
    drawerCaption:
      'Fictional parties throughout: Ashworth Capital Ltd, Brindle Systems Ltd, Vale Nominees Ltd. No real firms, clients or matters.',
  },
  {
    index: '②',
    name: 'Inject',
    detail: 'defects & ground truth emitted',
    meta: '4 mutations · keys written at injection',
    drawerCaption: 'Written at injection. Sealed from candidates.',
  },
  {
    index: '③',
    name: 'Consistency',
    detail: 'each trap · 10 runs × 3 models',
    meta: '9/10 · 10/10 — intended conclusion held',
    drawerCaption: 'Below 80% consistency, a scenario is rebuilt. This one held at 90.',
  },
  {
    index: '④',
    name: 'Solvability',
    detail: 'what can the model do alone?',
    meta: '0 scenarios solvable unaided · 1 confidently wrong',
    drawerCaption:
      'Given only the SPA and the transfer form, every tested model reported the seller information as consistent. The evidence that complicates it is elsewhere in the bundle.',
  },
  {
    index: '⑤',
    name: 'Attack review',
    detail: 'practitioner pass',
    meta: 'validated · corporate partner, 16 yrs PQE',
    drawerCaption:
      'Reviewed for realism, for trainee-appropriate scope, and to confirm no scenario requires legislation research or specialist knowledge.',
  },
  {
    index: '⑥',
    name: 'Lock & hash',
    detail: 'package frozen',
    meta: 'LAW-001 v0.1 · sha256 9f21…c4d9',
    drawerCaption: 'Every component hashed independently. A change to any one is a version bump.',
    manifest: [
      { component: 'bundle-review-rows', hash: '4a1e…77b2' },
      { component: 'instructions-and-checklist', hash: 'b930…1e5c' },
      { component: 'share-purchase-agreement', hash: 'c7d2…04af' },
      { component: 'disclosure-letter', hash: '2f88…9b31' },
      { component: 'register-of-members', hash: 'e510…c6d7' },
      { component: 'accruals-and-schedules', hash: '81b4…3fa0' },
      { component: 'assistant-system-prompt', hash: 'd642…8c19' },
      { component: 'ground-truth-keys', hash: 'a05f…b7e4' },
    ],
  },
]

export const lawRecordScenarioSchedule: RecordScenarioRow[] = [
  {
    slotId: 'SC-05',
    klass: 'CLEAN',
    row: 'H-01',
    outcome: 'Price agreed against clause 3.1 and the completion statement, then left alone.',
    category: 'clean-verified',
    sources: 'HAR-SPA-01 · HAR-CS-09',
  },
  {
    slotId: 'SC-06',
    klass: 'CLEAN',
    row: 'H-04',
    outcome: 'Schedule 5 checked line against the term sheet on all four limbs. No entry raised.',
    category: 'clean-verified',
    sources: 'HAR-TS-07',
  },
  {
    slotId: 'SC-04',
    klass: 'DISTRIBUTED',
    row: 'H-03',
    outcome:
      'Connected the lease consent requirement to the disclosure letter and escalated the outstanding consent ahead of completion.',
    category: 'resolved',
    sources: 'HAR-PS-06 · HAR-DL-02',
  },
  {
    slotId: 'SC-03',
    klass: 'AMBIGUOUS',
    row: 'H-05',
    outcome:
      'Spotted the date conflict, searched for later context, found the client instruction, recorded the checklist as stale.',
    category: 'resolved',
    sources: 'HAR-CI-05 · HAR-CL-10',
  },
  {
    slotId: 'SC-01',
    klass: 'LATENT ERROR',
    row: 'H-02',
    outcome:
      'Rejected the assistant’s clean read after opening the register; escalated the entity discrepancy without asserting a defect.',
    category: 'resolved',
    sources: 'HAR-ROM-03',
  },
  {
    slotId: 'SC-02',
    klass: 'LATENT ERROR',
    row: 'H-06',
    outcome:
      'The agreement was opened and clause 22 read, but the execution page was not turned to. The blank witness block was not raised.',
    category: 'missed',
    sources: '—',
  },
]

export const lawReconstruction: ReconstructionEvent[] = [
  { at: '09:14', actor: 'candidate', text: 'Opened HAR-SPA-01 — Share Purchase Agreement.' },
  { at: '09:18', actor: 'candidate', text: 'Shared HAR-SPA-01 with the assistant.' },
  {
    at: '09:20',
    actor: 'candidate',
    text: 'Asked: “Does the seller information appear consistent across these documents?”',
  },
  {
    at: '09:23',
    actor: 'assistant',
    text: '“Vale Nominees Ltd is named consistently as Seller and as transferor, and the share count and consideration agree between them.”',
  },
  {
    at: '09:26',
    actor: 'candidate',
    text: 'Opened HAR-ROM-03 — Register of Members. Did not respond to the assistant.',
  },
  { at: '09:28', actor: 'candidate', text: 'Shared HAR-ROM-03 with the assistant.' },
  { at: '09:29', actor: 'candidate', text: 'Asked: “How does this affect your conclusion?”' },
  {
    at: '09:30',
    actor: 'assistant',
    text: '“That changes my earlier read — the registered holder is Vale Nominees (No. 2) Ltd, a different registered name.”',
  },
  { at: '09:32', actor: 'candidate', text: 'Flagged H-02 · action: query supervising associate.' },
  {
    at: '09:33',
    actor: 'candidate',
    text: 'Wrote: “These appear to be different legal entities. There may be a legitimate nominee explanation, but I would not assume that without confirmation before completion.”',
  },
  {
    at: '09:34',
    actor: 'system',
    text: 'Sign-off recorded on H-02: material issues remain.',
  },
]

export const lawObservedBehaviour: { heading: string; body: string }[] = [
  {
    heading: 'Verified before accepting',
    body: 'The assistant returned a confident clean read on the seller question at 09:23. The candidate did not reply to it. They opened the register of members three minutes later and only then returned to the assistant — with the document, not with a follow-up question. The sequence matters: the check was not prompted by doubt the assistant raised, because it raised none.',
  },
  {
    heading: 'Calibrated rather than concluded',
    body: 'The written flag names the discrepancy, states that an ordinary explanation may exist, and asks for confirmation. It does not assert that the named transferor lacks title — which the bundle does not establish. This is the distinction between finding a problem and inventing one.',
  },
  {
    heading: 'Proportionate on the clean cases',
    body: 'Price and warranty limitations were each checked once against source and then left alone. The board minutes conflict was opened for 40 seconds and not pursued — the declaration and the enabling article are in the same document. No entry was raised on the undated transfer form.',
  },
  {
    heading: 'One material miss',
    body: 'The execution page was never reached. Clause 22 was read in the body of the agreement, but the candidate did not turn to the signature page to check the route had been completed. The blank witness block would have been visible immediately. This is the item to put to them in interview.',
  },
]
